if (!customElements.get('yc-wish')) {
  class YcWish extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.dataset.initialized = 'true';
      this.abortController = new AbortController();
      this.sectionRequestController = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
      this.handleFileChange = this.handleFileChange.bind(this);
      this.handleShopifyBlockSelect = this.handleShopifyBlockSelect.bind(this);

      this.cacheElements();
      this.state = {
        activeTab: this.dataset.defaultTab || 'stories',
        isDragging: false,
        startX: 0,
        scrollLeft: 0,
        dragged: false,
      };

      this.addEventListener('click', this.handleClick, {
        signal: this.abortController.signal,
      });
      this.addEventListener('keydown', this.handleKeydown, {
        signal: this.abortController.signal,
      });
      this.addEventListener('change', this.handleChange, {
        signal: this.abortController.signal,
      });
      this.addEventListener('submit', this.handleSubmit, {
        signal: this.abortController.signal,
      });
      window.addEventListener('popstate', this.handlePopState, {
        signal: this.abortController.signal,
      });
      document.addEventListener('shopify:block:select', this.handleShopifyBlockSelect, {
        signal: this.abortController.signal,
      });

      if (this.elements.uploadArea) {
        this.elements.uploadArea.addEventListener('click', () => this.elements.fileInput?.click(), {
          signal: this.abortController.signal,
        });
      }
      if (this.elements.fileInput) {
        this.elements.fileInput.addEventListener('change', this.handleFileChange, {
          signal: this.abortController.signal,
        });
      }

      if (this.elements.activityListWrapper) {
        this.elements.activityListWrapper.addEventListener('mousedown', (event) => this.startDragging(event), {
          signal: this.abortController.signal,
        });
        this.elements.activityListWrapper.addEventListener('mouseleave', () => this.stopDragging(), {
          signal: this.abortController.signal,
        });
        this.elements.activityListWrapper.addEventListener('mouseup', () => this.stopDragging(), {
          signal: this.abortController.signal,
        });
        this.elements.activityListWrapper.addEventListener('mousemove', (event) => this.drag(event), {
          signal: this.abortController.signal,
        });
      }

      this.initialize();
    }

    disconnectedCallback() {
      this.revokeStoryPreviewUrl();
      this.stopAllVideoCarousels();
      this.abortController?.abort();
      this.sectionRequestController?.abort();
    }

    cacheElements() {
      this.elements = {
        mainTabs: Array.from(this.querySelectorAll('[data-tab-target]')),
        panels: Array.from(this.querySelectorAll('[data-tab-panel]')),
        activityCards: Array.from(this.querySelectorAll('[data-activity-card]')),
        storyCountryFilter: this.querySelector('[data-story-filter="country"]'),
        storyTopicFilter: this.querySelector('[data-story-filter="topic"]'),
        storyForm: this.querySelector('[data-wish-form="story"]'),
        participationForm: this.querySelector('[data-wish-form="participation"]'),
        uploadArea: this.querySelector('[data-upload-area]'),
        fileInput: this.querySelector('[data-file-input]'),
        uploadPreview: this.querySelector('[data-upload-preview]'),
        uploadPlaceholder: this.querySelector('[data-upload-placeholder]'),
        uploadStatus: this.querySelector('[data-upload-status]'),
        activityListWrapper: this.querySelector('[data-activity-list-wrapper]'),
        videoCarousels: Array.from(this.querySelectorAll('[data-wish-videos]')),
        
        // Modal elements
        modal: this.querySelector('[data-wish-modal]'),
        modalCard: this.querySelector('#yc-wish-modal-card'),
        modalBadge: this.querySelector('[data-modal-badge]'),
        modalImage: this.querySelector('[data-modal-image]'),
        modalWish: this.querySelector('[data-modal-wish]'),
        modalSubmitter: this.querySelector('[data-modal-submitter]'),
        modalActivityName: this.querySelector('[data-modal-activity-name]'),
        modalActivityLocation: this.querySelector('[data-modal-activity-location]'),
        modalActivityLocationSeparator: this.querySelector('[data-modal-activity-location-separator]'),
        modalActivityDate: this.querySelector('[data-modal-activity-date]'),
      };
    }

    initialize() {
      this.storyUpload = {
        tempPhoto: null,
        uploadedFingerprint: '',
        previewObjectUrl: '',
        imageWidth: null,
        imageHeight: null,
      };
      this.switchMainTab(this.state.activeTab, {
        updateUrl: false,
      });
      this.syncStoryFilterLabel(this.elements.storyCountryFilter);
      this.syncStoryFilterLabel(this.elements.storyTopicFilter);
      this.querySelectorAll('.yc-wish__select').forEach((el) => {
        this.syncSelectWrapperLabel(el);
      });
      this.applyActivitySelection(this.getPreferredActivityId());
      this.initializeVideoCarousels();

      const isAnonymousRadio = this.querySelector('input[name="isAnonymous"]:checked');
      if (isAnonymousRadio) {
        this.toggleNameFields(isAnonymousRadio.value === 'false');
      }
    }

    initializeVideoCarousels() {
      this.videoCarouselStates = new Map();

      this.elements.videoCarousels.forEach((carousel) => {
        const items = Array.from(carousel.querySelectorAll('[data-wish-videos-item]'));
        if (items.length === 0) {
          return;
        }

        const state = {
          carousel,
          items,
          dots: Array.from(carousel.querySelectorAll('[data-wish-videos-dot]')),
          currentIndex: 0,
          total: items.length,
          autoRotate: carousel.dataset.autoRotate === 'true',
          changeSlidesSpeed: Number.parseInt(carousel.dataset.changeSlidesSpeed, 10) * 1000 || 5000,
          autoplayInterval: null,
        };
        this.videoCarouselStates.set(carousel, state);

        const trackContainer = carousel.querySelector('[data-wish-videos-track-container]');
        if (trackContainer) {
          let touchStartX = 0;

          trackContainer.addEventListener('mouseenter', () => this.stopVideoCarousel(state), {
            signal: this.abortController.signal,
          });
          trackContainer.addEventListener('mouseleave', () => this.startVideoCarousel(state), {
            signal: this.abortController.signal,
          });
          trackContainer.addEventListener('touchstart', (event) => {
            this.stopVideoCarousel(state);
            touchStartX = event.changedTouches[0]?.screenX || 0;
          }, {
            passive: true,
            signal: this.abortController.signal,
          });
          trackContainer.addEventListener('touchend', (event) => {
            const touchEndX = event.changedTouches[0]?.screenX || 0;
            if (touchStartX - touchEndX > 50) {
              state.currentIndex = (state.currentIndex + 1) % state.total;
            } else if (touchEndX - touchStartX > 50) {
              state.currentIndex = (state.currentIndex - 1 + state.total) % state.total;
            }
            this.updateVideoCarousel(state);
            this.startVideoCarousel(state);
          }, {
            passive: true,
            signal: this.abortController.signal,
          });
        }

        items.forEach((item, itemIndex) => {
          item.addEventListener('click', (event) => {
            if (itemIndex === state.currentIndex) {
              return;
            }

            event.preventDefault();
            state.currentIndex = itemIndex;
            this.updateVideoCarousel(state);
          }, {
            signal: this.abortController.signal,
          });

          const muteButton = item.querySelector('[data-wish-videos-mute]');
          const playButton = item.querySelector('[data-wish-videos-play]');
          const video = item.querySelector('video');
          if (muteButton && video) {
            muteButton.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();
              video.muted = !video.muted;
              const muteIcon = item.querySelector('.icon-mute');
              const unmuteIcon = item.querySelector('.icon-unmute');
              if (muteIcon) muteIcon.style.display = video.muted ? 'block' : 'none';
              if (unmuteIcon) unmuteIcon.style.display = video.muted ? 'none' : 'block';
            }, {
              signal: this.abortController.signal,
            });
          }

          if (playButton && video) {
            playButton.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();

              if (itemIndex !== state.currentIndex) {
                state.currentIndex = itemIndex;
                this.updateVideoCarousel(state);
                return;
              }

              if (video.paused) {
                video.play()
                  .then(() => item.classList.toggle('is-playing', true))
                  .catch(() => item.classList.toggle('is-playing', false));
              } else {
                video.pause();
                item.classList.toggle('is-playing', false);
              }
            }, {
              signal: this.abortController.signal,
            });
          }
        });

        state.dots.forEach((dot, dotIndex) => {
          dot.addEventListener('click', () => {
            state.currentIndex = dotIndex;
            this.updateVideoCarousel(state);
            this.startVideoCarousel(state);
          }, {
            signal: this.abortController.signal,
          });
        });

        this.updateVideoCarousel(state);
        this.startVideoCarousel(state);
      });

      if (this.videoCarouselStates.size > 0) {
        window.addEventListener('resize', () => {
          this.videoCarouselStates.forEach((state) => this.updateVideoCarousel(state));
        }, {
          signal: this.abortController.signal,
        });
      }
    }

    updateVideoCarousel(state) {
      const isMobile = window.innerWidth < 768;
      const baseWidth = isMobile ? 280 : 324;
      const gap = isMobile ? 16 : 24;
      const sideScale = 0.9;
      const visualSideWidth = baseWidth * sideScale;
      const panel = state.carousel.closest('[data-tab-panel]');
      const isVisible = !panel?.hidden;

      state.items.forEach((item, itemIndex) => {
        let offset = itemIndex - state.currentIndex;
        if (offset < -Math.floor(state.total / 2)) offset += state.total;
        if (offset > Math.floor(state.total / 2)) offset -= state.total;

        const distance = Math.abs(offset);
        const direction = Math.sign(offset);
        const firstOffset = (baseWidth / 2) + gap + (visualSideWidth / 2);
        const extraOffset = (distance - 1) * (visualSideWidth + gap);
        const translateX = distance === 0 ? 0 : direction * (firstOffset + extraOffset);
        const video = item.querySelector('video');

        item.classList.toggle('is-active', distance === 0);
        item.style.transform = `translate(calc(-50% + ${translateX}px), -50%) scale(${distance === 0 ? 1 : sideScale})`;
        item.style.zIndex = distance === 0 ? 10 : 5;
        item.style.opacity = distance > 2 ? 0 : 1;
        item.style.pointerEvents = distance > 2 ? 'none' : 'auto';

        if (!video) {
          item.classList.toggle('is-playing', false);
          return;
        }

        if (distance === 0 && isVisible) {
          if (video.paused) {
            video.play()
              .then(() => item.classList.toggle('is-playing', true))
              .catch(() => item.classList.toggle('is-playing', false));
          } else {
            item.classList.toggle('is-playing', true);
          }
        } else if (!video.paused || video.currentTime > 0) {
          video.pause();
          video.currentTime = 0;
          item.classList.toggle('is-playing', false);
        } else {
          item.classList.toggle('is-playing', false);
        }
      });

      state.dots?.forEach((dot, dotIndex) => {
        const isActive = dotIndex === state.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    startVideoCarousel(state) {
      const panel = state.carousel.closest('[data-tab-panel]');
      if (!state.autoRotate || panel?.hidden) {
        return;
      }

      this.stopVideoCarousel(state);
      state.autoplayInterval = window.setInterval(() => {
        state.currentIndex = (state.currentIndex + 1) % state.total;
        this.updateVideoCarousel(state);
      }, state.changeSlidesSpeed);
    }

    stopVideoCarousel(state) {
      if (state.autoplayInterval !== null) {
        window.clearInterval(state.autoplayInterval);
        state.autoplayInterval = null;
      }
    }

    syncVideoCarousels() {
      this.videoCarouselStates?.forEach((state) => {
        this.updateVideoCarousel(state);
        const panel = state.carousel.closest('[data-tab-panel]');
        if (panel?.hidden) {
          this.stopVideoCarousel(state);
        } else {
          this.startVideoCarousel(state);
        }
      });
    }

    stopAllVideoCarousels() {
      this.videoCarouselStates?.forEach((state) => this.stopVideoCarousel(state));
    }

    handleClick(event) {
      if (event.target.closest('[data-stop-propagation]')) {
        return;
      }

      const activityLinkTrigger = event.target.closest('[data-activity-link-trigger]');
      if (activityLinkTrigger && this.contains(activityLinkTrigger)) {
        const href = activityLinkTrigger.getAttribute('data-activity-link') || '';
        if (href) {
          window.location.assign(href);
          return;
        } else {
          event.preventDefault();
          event.stopPropagation();
          const activityCard = activityLinkTrigger.closest('[data-activity-card]');
          if (activityCard) {
            const activityId = activityCard.getAttribute('data-activity-id') || '';
            this.applyActivitySelection(activityId);
            
            const scrollToForm = () => {
              const form = this.querySelector('#yc-wish-participation-form');
              if (form) {
                form.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            };
            
            scrollToForm();

            this.requestSection({
              wish_tab: 'wishes',
              wish_activity: activityId,
              wish_participation_page: 1,
            }, { preventScrollRestore: true }).then(() => {
              requestAnimationFrame(scrollToForm);
            });
            return;
          }
        }
      }

      if (this.state.dragged) {
        this.state.dragged = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const actionTarget = event.target.closest('[data-action]');
      if (!actionTarget || !this.contains(actionTarget)) {
        return;
      }

      const action = actionTarget.getAttribute('data-action');

      if (action === 'switch-main-tab') {
        this.switchMainTab(actionTarget.getAttribute('data-tab-target') || 'stories');
        return;
      }

      if (action === 'load-more-stories') {
        this.requestSection({
          wish_tab: 'stories',
          wish_story_page: this.getQueryNumber('wish_story_page', 1) + 1,
        });
        return;
      }

      if (action === 'select-activity') {
        const activityId = actionTarget.getAttribute('data-activity-id') || '';
        this.applyActivitySelection(activityId);
        
        // Uncomment the lines below for debug: pop up modal when activity is selected and stop section reload
        // this.debugShowSuccessModal();
        // return;

        this.requestSection({
          wish_tab: 'wishes',
          wish_activity: activityId,
          wish_participation_page: 1,
        });

        return;
      }

      if (action === 'close-modal') {
        if (this.elements.modal) {
          this.elements.modal.hidden = true;
        }
        return;
      }

      if (action === 'save-image') {
        this.downloadImageNativeCanvas();
        return;
      }

      if (action === 'share-image') {
        this.shareImageNativeCanvas();
        return;
      }

      if (action === 'filter-participation-topic') {
        const topic = actionTarget.getAttribute('data-topic-filter') || 'all';
        this.requestSection({
          wish_tab: 'wishes',
          wish_participation_topic: topic === 'all' ? '' : topic,
          wish_participation_page: 1,
        });
        return;
      }

      if (action === 'toggle-story-sort') {
        const currentSort = actionTarget.getAttribute('data-story-sort');
        const nextSort = currentSort === 'newest' ? 'oldest' : 'newest';
        this.requestSection({
          wish_tab: 'stories',
          wish_story_sort: nextSort,
          wish_story_page: 1,
        });
        return;
      }

      if (action === 'toggle-participation-sort') {
        const currentSort = actionTarget.getAttribute('data-participation-sort');
        const nextSort = currentSort === 'newest' ? 'oldest' : 'newest';
        this.requestSection({
          wish_tab: 'wishes',
          wish_participation_sort: nextSort,
          wish_participation_page: 1,
        });
        return;
      }

      if (action === 'load-more-participations') {
        this.requestSection({
          wish_tab: 'wishes',
          wish_participation_page: this.getQueryNumber('wish_participation_page', 1) + 1,
        });
      }
    }

    handleChange(event) {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      if (target.matches('.yc-wish__select')) {
        this.syncSelectWrapperLabel(target);
      }

      if (target.matches('[data-story-filter="country"], [data-story-filter="topic"]')) {
        this.syncStoryFilterLabel(target);
        this.requestSection({
          wish_tab: 'stories',
          wish_story_country: this.elements.storyCountryFilter?.value || '',
          wish_story_topic: this.elements.storyTopicFilter?.value || '',
          wish_story_page: 1,
        });
        return;
      }

      if (target.matches('input[name="isAnonymous"]')) {
        this.toggleNameFields(target.value === 'false');
      }
    }

    handleKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      const actionTarget = event.target.closest('[data-action="select-activity"]');
      if (!actionTarget || !this.contains(actionTarget)) {
        return;
      }

      event.preventDefault();
      this.handleClick(event);
    }

    handlePopState() {
      this.refreshSection(this.buildSectionUrl(), {
        updateHistory: false,
      });
    }

    handleShopifyBlockSelect(event) {
      if (!(event.target instanceof Element) || !this.contains(event.target)) {
        return;
      }

      const panel = event.target.closest('[data-tab-panel]');
      const tabId = panel?.getAttribute('data-tab-panel');
      if (tabId) {
        this.switchMainTab(tabId, { updateUrl: false });
      }
    }

    switchMainTab(tabId, { updateUrl = true } = {}) {
      const availableTabs = new Set(
        this.elements.panels
          .map((panel) => panel.getAttribute('data-tab-panel') || '')
          .filter(Boolean),
      );
      const fallbackTab = this.elements.panels[0]?.getAttribute('data-tab-panel') || 'stories';

      this.state.activeTab = availableTabs.has(tabId || '') ? tabId : fallbackTab;

      this.elements.mainTabs.forEach((button) => {
        const isActive = button.getAttribute('data-tab-target') === this.state.activeTab;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.elements.panels.forEach((panel) => {
        const isActive = panel.getAttribute('data-tab-panel') === this.state.activeTab;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });
      this.syncVideoCarousels();

      if (updateUrl) {
        this.updateBrowserUrl({
          wish_tab: this.state.activeTab,
        });
      }
    }

    syncStoryFilterLabel(selectElement) {
      if (!(selectElement instanceof HTMLSelectElement)) {
        return;
      }

      const filterItem = selectElement.closest('.yc-wish__filter-item');
      if (!filterItem) {
        return;
      }

      filterItem.dataset.value = selectElement.options[selectElement.selectedIndex]?.text || '';
      if (selectElement.dataset.storyFilter === 'topic') {
        filterItem.classList.toggle('is-active', Boolean(selectElement.value));
      }
    }

    syncSelectWrapperLabel(selectElement) {
      if (!(selectElement instanceof HTMLSelectElement)) {
        return;
      }
      const wrapper = selectElement.closest('.yc-wish__select-wrapper');
      if (!wrapper) {
        return;
      }
      wrapper.dataset.value = selectElement.options[selectElement.selectedIndex]?.text || '';
      wrapper.classList.toggle('is-placeholder', !selectElement.value);
    }

    getActiveActivityCard() {
      return this.querySelector('[data-activity-card].is-active, [data-activity-card][aria-pressed="true"]');
    }

    getPreferredActivityId(urlLike = null) {
      const selectedFromUrl = (() => {
        if (!urlLike) {
          return '';
        }

        try {
          const url = urlLike instanceof URL ? urlLike : new URL(String(urlLike), window.location.origin);
          return url.searchParams.get('wish_activity') || '';
        } catch (error) {
          return '';
        }
      })();

      if (selectedFromUrl) {
        return selectedFromUrl;
      }

      if (this.dataset.selectedActivityId) {
        return this.dataset.selectedActivityId;
      }

      return this.getQueryValue('wish_activity', '');
    }

    applyActivitySelection(activityId = '') {
      const cards = this.elements.activityCards || [];
      if (cards.length === 0) {
        const fallbackActivityId =
          activityId ||
          this.dataset.selectedActivityId ||
          this.elements.participationForm?.querySelector('input[name="activityId"]')?.value ||
          '';
        this.dataset.selectedActivityId = fallbackActivityId;
        this.syncSelectedActivityFields(null, fallbackActivityId);
        return null;
      }

      const currentActiveCard = cards.find(
        (card) => card.classList.contains('is-active') || card.getAttribute('aria-pressed') === 'true',
      );
      const fallbackCard = currentActiveCard || cards[0] || null;
      const targetId = activityId || this.dataset.selectedActivityId || fallbackCard?.getAttribute('data-activity-id') || '';
      const activeCard =
        cards.find((card) => card.getAttribute('data-activity-id') === targetId) || fallbackCard;

      cards.forEach((card) => {
        const isActive = card === activeCard;
        card.classList.toggle('is-active', isActive);
        card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.dataset.selectedActivityId = activeCard?.getAttribute('data-activity-id') || '';
      this.syncSelectedActivityFields(activeCard);
      return activeCard;
    }

    syncSelectedActivityFields(activeCard, fallbackActivityId = '') {
      const hiddenInput = this.elements.participationForm?.querySelector('input[name="activityId"]');
      const submitButton = this.elements.participationForm?.querySelector('button[type="submit"]');
      const selectedActivityId = activeCard?.getAttribute('data-activity-id') || fallbackActivityId;
      const hasActivity = Boolean(selectedActivityId);

      if (hiddenInput instanceof HTMLInputElement) {
        hiddenInput.value = selectedActivityId;
      }

      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = !hasActivity;
      }
    }

    buildBrowserUrl(patch = {}) {
      const url = new URL(window.location.href);

      Object.entries(patch).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          url.searchParams.delete(key);
          return;
        }

        url.searchParams.set(key, String(value));
      });

      return url;
    }

    buildSectionUrl(patch = {}) {
      const url = this.buildBrowserUrl(patch);
      if (this.dataset.sectionId) {
        url.searchParams.set('section_id', this.dataset.sectionId);
      }
      return url;
    }

    updateBrowserUrl(patch = {}) {
      const url = this.buildBrowserUrl(patch);
      window.history.replaceState(
        {
          wishSectionId: this.dataset.sectionId || '',
        },
        '',
        `${url.pathname}${url.search}${url.hash}`,
      );
    }

    getQueryValue(key, fallback = '') {
      const value = new URL(window.location.href).searchParams.get(key);
      return value ?? fallback;
    }

    getQueryNumber(key, fallback = 1) {
      const value = Number.parseInt(this.getQueryValue(key, String(fallback)), 10);
      return Number.isNaN(value) ? fallback : value;
    }

    async requestSection(patch = {}, options = {}) {
      await this.refreshSection(this.buildSectionUrl(patch), options);
    }

    async refreshSection(sectionUrl, { updateHistory = true, preventScrollRestore = false } = {}) {
      this.sectionRequestController?.abort();
      this.sectionRequestController = new AbortController();

      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      const preferredActivityId =
        this.getPreferredActivityId(sectionUrl) ||
        this.getActiveActivityCard()?.getAttribute('data-activity-id') ||
        '';

      try {
        this.classList.add('is-loading');
        const response = await fetch(sectionUrl.toString(), {
          credentials: 'same-origin',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          signal: this.sectionRequestController.signal,
        });

        if (!response.ok) {
          throw new Error(`Section request failed: ${response.status}`);
        }

        const html = await response.text();
        const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
        const nextElement = parsedHtml.querySelector('yc-wish');

        if (!(nextElement instanceof HTMLElement)) {
          throw new Error('Unable to find refreshed wish section.');
        }

        if (preferredActivityId) {
          nextElement.dataset.selectedActivityId = preferredActivityId;
        }

        if (updateHistory) {
          const browserUrl = new URL(sectionUrl);
          browserUrl.searchParams.delete('section_id');
          window.history.replaceState(
            {
              wishSectionId: this.dataset.sectionId || '',
            },
            '',
            `${browserUrl.pathname}${browserUrl.search}${browserUrl.hash}`,
          );
        }

        this.replaceWith(nextElement);
        if (!preventScrollRestore) {
          window.requestAnimationFrame(() => {
            window.scrollTo(scrollX, scrollY);
          });
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }

        console.error(error);
      } finally {
        this.classList.remove('is-loading');
      }
    }

    toggleNameFields(show) {
      const nameFields = this.querySelector('[data-name-fields]');
      if (!nameFields) {
        return;
      }

      nameFields.hidden = !show;
      const inputs = nameFields.querySelectorAll('input, select');
      inputs.forEach((input) => {
        if (show) {
          input.setAttribute('required', '');
        } else {
          input.removeAttribute('required');
        }
      });
    }

    async handleFileChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) {
        return;
      }

      const file = input.files?.[0];
      if (!file) {
        this.clearStoryUploadSelection();
        return;
      }

      let previewUrl = '';

      try {
        this.validateStoryFile(file);
        previewUrl = URL.createObjectURL(file);
        const dimensions = await this.loadImageDimensions(previewUrl);

        this.revokeStoryPreviewUrl();
        this.storyUpload = {
          tempPhoto: null,
          uploadedFingerprint: '',
          previewObjectUrl: previewUrl,
          imageWidth: dimensions.width,
          imageHeight: dimensions.height,
        };

        if (this.elements.uploadPreview) {
          this.elements.uploadPreview.src = previewUrl;
          this.elements.uploadPreview.hidden = false;
        }
        if (this.elements.uploadPlaceholder) {
          this.elements.uploadPlaceholder.hidden = true;
        }
        this.setUploadStatus('Image ready. It uploads securely when you submit.', 'neutral');
      } catch (error) {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        input.value = '';
        this.clearStoryUploadSelection();
        this.setUploadStatus(
          error instanceof Error ? error.message : 'Unable to read this image. Please try another file.',
          'error',
        );
      }
    }

    handleSubmit(event) {
      const form = event.target;

      if (!(form instanceof HTMLFormElement) || !this.contains(form)) {
        return;
      }

      const formType = form.getAttribute('data-wish-form');
      if (!formType) {
        return;
      }

      event.preventDefault();

      if (form.dataset.submitting === 'true') {
        return;
      }

      if (formType === 'story') {
        this.submitStoryForm(form);
        return;
      }

      if (formType === 'participation') {
        this.submitParticipationForm(form);
      }
    }

    async submitStoryForm(form) {
      if (!form.reportValidity()) {
        return;
      }

      const formData = new FormData(form);
      const country = this.getSelectedCountry(form.elements.namedItem('countryCode'));
      const contactEmail = String(formData.get('contactEmail') || '').trim();
      const topic = String(formData.get('topic') || '').trim();

      let photoPayload;

      try {
        this.setSubmitting(form, true);
        this.setFormStatus('story', '', 'neutral');
        photoPayload = await this.resolveStoryPhotoPayload();
      } catch (error) {
        this.setFormStatus(
          'story',
          error instanceof Error ? error.message : 'Please add a photo before submitting.',
          'error',
        );
        return;
      } finally {
        if (!photoPayload) {
          this.setSubmitting(form, false);
        }
      }

      const payload = this.buildPayload(
        {
          photo: photoPayload,
          name: String(formData.get('name') || '').trim(),
          country,
          topic,
          durationText: String(formData.get('durationText') || '').trim(),
          storyContent: String(formData.get('storyContent') || '').trim(),
          contactEmail,
          subscribeSundayLetter: formData.get('subscribeSundayLetter') === 'true',
        },
        { email: contactEmail },
      );

      await this.submitForm({
        form,
        formType: 'story',
        url: this.dataset.storySubmitUrl || '/apps/kailashenergy/wish/stories',
        payload,
        manageSubmitting: false,
        onSuccess: (result) => {
          form.reset();
          this.clearStoryUploadSelection();
          this.setUploadStatus('', 'neutral');
          this.setFormStatus(
            'story',
            result?.data?.id
              ? `Story submitted successfully. Reference: ${result.data.id}`
              : 'Story submitted successfully.',
            'success',
          );
        },
      });
    }

    async submitParticipationForm(form) {
      const activityIdInput = form.elements.namedItem('activityId');
      const activityId =
        activityIdInput instanceof HTMLInputElement ? activityIdInput.value.trim() : '';

      if (!activityId) {
        this.setFormStatus('participation', 'Please choose an activity before submitting.', 'error');
        return;
      }

      if (!form.reportValidity()) {
        return;
      }

      const formData = new FormData(form);
      const country = this.getSelectedCountry(form.elements.namedItem('countryCode'));
      const contactEmail = String(formData.get('contactEmail') || '').trim();
      const payload = this.buildPayload(
        {
          activityId,
          wishContent: String(formData.get('wishContent') || '').trim(),
          isAnonymous: formData.get('isAnonymous') === 'true',
          submitterName: formData.get('isAnonymous') === 'true' ? 'Anonymous' : String(formData.get('submitterName') || '').trim(),
          country,
          contactEmail,
          subscribeSundayLetter: formData.get('subscribeSundayLetter') === 'true',
        },
        { email: contactEmail },
      );

      await this.submitForm({
        form,
        formType: 'participation',
        url: this.dataset.participationSubmitUrl || '/apps/kailashenergy/wish/activity-participations',
        payload,
        onSuccess: (result) => {
          form.reset();
          const activeCard = this.getActiveActivityCard();
          this.syncSelectedActivityFields(activeCard);
          const submissionNumber = result?.data?.submissionNumber;
          this.setFormStatus(
            'participation',
            submissionNumber
              ? `Wish submitted successfully. No. ${submissionNumber}`
              : result?.data?.id
                ? `Wish submitted successfully. Reference: ${result.data.id}`
                : 'Wish submitted successfully.',
            'success',
          );
          
          this.showSuccessModal({
            submissionNumber,
            wishContent: payload.wishContent,
            submitterName: payload.submitterName,
            countryName: country.name,
            activityName: activeCard?.getAttribute('data-activity-name') || '',
            activityDate: activeCard?.getAttribute('data-activity-date') || '',
            activityLocation: activeCard?.getAttribute('data-activity-location') || '',
            activityDateDescription: activeCard?.getAttribute('data-activity-date-description') || '',
            activityImageUrl: activeCard?.querySelector('img')?.src || ''
          });
        },
      });
    }

    async submitForm({ form, formType, url, payload, onSuccess, manageSubmitting = true }) {
      if (manageSubmitting) {
        this.setSubmitting(form, true);
        this.setFormStatus(formType, '', 'neutral');
      }

      try {
        const result = await this.postJson(url, payload);
        onSuccess(result);
      } catch (error) {
        this.setFormStatus(
          formType,
          error instanceof Error ? error.message : 'Submission failed. Please try again.',
          'error',
        );
      } finally {
        this.setSubmitting(form, false);
      }
    }

    async postJson(url, payload) {
      const helper = window.YcAppProxy;

      if (helper?.postJson) {
        return helper.postJson(url, payload);
      }

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      let json = null;

      try {
        json = await response.json();
      } catch (error) {
        json = null;
      }

      if (!response.ok || json?.ok === false) {
        const errorMessage =
          json?.error?.message || json?.message || 'Submission failed. Please try again.';
        throw new Error(errorMessage);
      }

      return json;
    }

    async resolveStoryPhotoPayload() {
      const file = this.elements.fileInput?.files?.[0];
      if (!file) {
        throw new Error('Please add a photo before submitting your story.');
      }

      const fingerprint = this.createFileFingerprint(file);
      if (this.storyUpload.tempPhoto && this.storyUpload.uploadedFingerprint === fingerprint) {
        return this.storyUpload.tempPhoto;
      }

      const tempPhoto = await this.uploadStoryPhoto(file);
      this.storyUpload.tempPhoto = tempPhoto;
      this.storyUpload.uploadedFingerprint = fingerprint;
      return tempPhoto;
    }

    async uploadStoryPhoto(file) {
      const moduleEntityId = this.generateStorageEntityId('story-upload');
      this.setUploadStatus('Preparing secure upload...', 'neutral');

      const sessionResult = await this.postJson(
        this.dataset.uploadSessionUrl || '/apps/kailashenergy/storage/uploads/session',
        {
          module: 'wish',
          moduleEntityType: 'story',
          moduleEntityId,
          resourceType: 'stories',
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          visibility: 'protected',
        },
      );
      const uploadSession = sessionResult?.data || {};

      if (!uploadSession.uploadSessionId || !uploadSession.uploadUrl) {
        throw new Error('Unable to create an upload session for this image.');
      }

      this.setUploadStatus('Uploading image...', 'neutral');
      await this.uploadBinary(uploadSession, file);

      this.setUploadStatus('Saving uploaded image...', 'neutral');
      const completeResult = await this.postJson(
        this.dataset.uploadCompleteUrl || '/apps/kailashenergy/storage/uploads/complete',
        {
          uploadSessionId: uploadSession.uploadSessionId,
          sizeBytes: file.size,
        },
      );
      const completedFile = completeResult?.data?.file || null;

      this.setUploadStatus('Image uploaded successfully.', 'success');

      return {
        resourceType: 'image',
        alt: file.name,
        createdAt: completedFile?.createdAt || new Date().toISOString(),
        fileStatus: 'READY',
        mimeType: completedFile?.mimeType || file.type || 'application/octet-stream',
        filename: completedFile?.originalFilename || file.name,
        path: completedFile?.objectKey || uploadSession.objectKey || '',
        folderPath: this.getFolderPath(completedFile?.objectKey || uploadSession.objectKey || ''),
        resourceUrl: '',
        previewUrl: '',
        width: this.storyUpload.imageWidth,
        height: this.storyUpload.imageHeight,
      };
    }

    async uploadBinary(uploadSession, file) {
      const response = await fetch(uploadSession.uploadUrl, {
        method: uploadSession.uploadMethod || 'PUT',
        headers: uploadSession.uploadHeaders || {},
        body: file,
      });

      if (!response.ok) {
        throw new Error('Image upload failed. Please try again.');
      }
    }

    buildPayload(basePayload, identity = {}) {
      const helper = window.YcAppProxy;

      if (helper?.buildModuleAppProxyPayload) {
        return helper.buildModuleAppProxyPayload({
          moduleKey: 'yc-wish',
          basePayload,
          identity,
        });
      }

      return Object.entries(identity || {}).reduce(
        (result, [key, value]) => {
          if (value === undefined || value === null || value === '') {
            return result;
          }

          result[key] = value;
          return result;
        },
        { ...(basePayload || {}) },
      );
    }

    buildPhotoPayload(url, altText) {
      const normalizedUrl = url.trim();
      const filename = this.getFilenameFromUrl(normalizedUrl);

      return {
        resourceType: 'image',
        alt: altText || 'Wish story cover',
        mimeType: this.getMimeTypeFromUrl(normalizedUrl),
        filename,
        resourceUrl: normalizedUrl,
        previewUrl: normalizedUrl,
      };
    }

    validateStoryFile(file) {
      const maximumUploadBytes = 25 * 1024 * 1024;
      if (!file.type || !file.type.startsWith('image/')) {
        throw new Error('Please choose an image file.');
      }

      if (file.size < 1) {
        throw new Error('The selected image is empty.');
      }

      if (file.size > maximumUploadBytes) {
        throw new Error('Please upload an image smaller than 25MB.');
      }
    }

    async loadImageDimensions(previewUrl) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
          resolve({
            width: image.naturalWidth || null,
            height: image.naturalHeight || null,
          });
        };
        image.onerror = () => {
          reject(new Error('Unable to read this image. Please try another file.'));
        };
        image.src = previewUrl;
      });
    }

    createFileFingerprint(file) {
      return [file.name, file.size, file.lastModified, file.type].join(':');
    }

    generateStorageEntityId(prefix) {
      const rawId =
        window.crypto && typeof window.crypto.randomUUID === 'function'
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.round(Math.random() * 10000)}`;
      return `${prefix}-${rawId}`.replace(/[^A-Za-z0-9._-]/g, '-');
    }

    getFolderPath(path) {
      if (!path || !path.includes('/')) {
        return '';
      }

      return path.split('/').slice(0, -1).join('/');
    }

    getFilenameFromUrl(url) {
      try {
        const parsedUrl = new URL(url);
        const filename = parsedUrl.pathname.split('/').filter(Boolean).pop();
        return filename || 'wish-story-image.jpg';
      } catch (error) {
        return 'wish-story-image.jpg';
      }
    }

    getMimeTypeFromUrl(url) {
      const extension = (url.split('.').pop() || '').toLowerCase();

      if (extension === 'png') {
        return 'image/png';
      }

      if (extension === 'webp') {
        return 'image/webp';
      }

      if (extension === 'gif') {
        return 'image/gif';
      }

      return 'image/jpeg';
    }

    getSelectedCountry(field) {
      if (!(field instanceof HTMLSelectElement)) {
        return {
          name: '',
          isoCode: '',
        };
      }

      const selectedOption = field.options[field.selectedIndex];

      return {
        name:
          selectedOption?.getAttribute('data-country-name') ||
          selectedOption?.textContent?.trim() ||
          '',
        isoCode: field.value,
      };
    }

    setSubmitting(form, isSubmitting) {
      const submitButton = form.querySelector('button[type="submit"]');
      const activeActivity = this.getActiveActivityCard();
      const controls = Array.from(form.elements || []).filter(
        (element) => element instanceof HTMLButtonElement || element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement,
      );

      form.dataset.submitting = isSubmitting ? 'true' : 'false';
      form.classList.toggle('is-submitting', isSubmitting);
      form.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');

      controls.forEach((control) => {
        if (isSubmitting) {
          control.dataset.disabledBeforeSubmit = control.disabled ? 'true' : 'false';
          control.disabled = true;
          return;
        }

        const wasDisabled = control.dataset.disabledBeforeSubmit === 'true';
        control.disabled = wasDisabled;
        delete control.dataset.disabledBeforeSubmit;
      });

      if (submitButton instanceof HTMLButtonElement) {
        const submitText = submitButton.querySelector('[data-submit-text]');
        const defaultLabel =
          submitButton.dataset.submitLabel ||
          (submitText instanceof HTMLElement ? submitText.textContent?.trim() || '' : submitButton.textContent?.trim() || '');
        const submittingLabel = submitButton.dataset.submittingLabel || 'Submitting...';

        submitButton.dataset.submitLabel = defaultLabel;

        if (isSubmitting) {
          submitButton.disabled = true;
        } else if (form === this.elements.participationForm) {
          submitButton.disabled = !activeActivity;
        } else {
          submitButton.disabled = false;
        }

        submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');

        if (submitText instanceof HTMLElement) {
          submitText.textContent = isSubmitting ? submittingLabel : defaultLabel;
        } else {
          submitButton.textContent = isSubmitting ? submittingLabel : defaultLabel;
        }
      }

      if (form === this.elements.storyForm && this.elements.fileInput instanceof HTMLInputElement) {
        this.elements.fileInput.disabled = isSubmitting;
      }
    }

    setFormStatus(formType, message, status) {
      const statusNode = this.querySelector(`[data-form-status="${formType}"]`);

      if (!(statusNode instanceof HTMLElement)) {
        return;
      }

      statusNode.hidden = !message;
      statusNode.textContent = message;
      statusNode.dataset.status = status;
    }

    setUploadStatus(message, status) {
      const statusNode = this.elements.uploadStatus;

      if (!(statusNode instanceof HTMLElement)) {
        return;
      }

      statusNode.hidden = !message;
      statusNode.textContent = message;
      statusNode.dataset.status = status;
    }

    clearStoryUploadSelection() {
      this.revokeStoryPreviewUrl();
      this.storyUpload = {
        tempPhoto: null,
        uploadedFingerprint: '',
        previewObjectUrl: '',
        imageWidth: null,
        imageHeight: null,
      };

      if (this.elements.fileInput instanceof HTMLInputElement) {
        this.elements.fileInput.value = '';
      }

      if (this.elements.uploadPreview) {
        this.elements.uploadPreview.src = '';
        this.elements.uploadPreview.hidden = true;
      }

      if (this.elements.uploadPlaceholder) {
        this.elements.uploadPlaceholder.hidden = false;
      }
    }

    revokeStoryPreviewUrl() {
      if (this.storyUpload?.previewObjectUrl) {
        URL.revokeObjectURL(this.storyUpload.previewObjectUrl);
      }
    }

    debugShowSuccessModal() {
      const activeCard = this.getActiveActivityCard();
      this.showSuccessModal({
        submissionNumber: 888,
        wishContent: "For my late father — that he rests.",
        submitterName: "Ken",
        countryName: "Osaka",
        activityName: activeCard?.getAttribute('data-activity-name') || 'Raised on Mount Kailash',
        activityDate: activeCard?.getAttribute('data-activity-date') || '2024-05-31',
        activityLocation: activeCard?.getAttribute('data-activity-location') || 'Tibet',
        activityDateDescription: activeCard?.getAttribute('data-activity-date-description') || 'SAGA DAWA',
        activityImageUrl: activeCard?.querySelector('img')?.src || ''
      });
    }

    showSuccessModal(data) {
      if (!this.elements.modal) return;
      
      const {
        submissionNumber,
        wishContent,
        submitterName,
        countryName,
        activityName,
        activityDate,
        activityLocation,
        activityDateDescription,
        activityImageUrl
      } = data;
      
      let formattedDate = activityDate;
      if (activityDate) {
        const parts = activityDate.split('-');
        if (parts.length === 3) {
          const d = new Date(activityDate);
          if (!isNaN(d.getTime())) {
            const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
            formattedDate = `${months[d.getMonth()]} ${d.getDate()}`;
          }
        }
      }
      
      const badgeText = submissionNumber
        ? `No ${String(submissionNumber)}<br>WISH`
        : 'WISH';
      
      if (this.elements.modalBadge) this.elements.modalBadge.innerHTML = badgeText;
      if (this.elements.modalImage) {
        if (activityImageUrl) {
          this.elements.modalImage.src = activityImageUrl;
        } else {
          this.elements.modalImage.removeAttribute('src');
        }
      }
      if (this.elements.modalWish) this.elements.modalWish.textContent = wishContent;
      
      let submitterText = submitterName;
      if (countryName && submitterName !== 'Anonymous') {
        submitterText += `, ${countryName}`;
      }
      if (this.elements.modalSubmitter) this.elements.modalSubmitter.textContent = submitterText;
      
      if (this.elements.modalActivityName) this.elements.modalActivityName.textContent = activityName;
      if (this.elements.modalActivityLocation) this.elements.modalActivityLocation.textContent = activityLocation;
      if (this.elements.modalActivityDate) this.elements.modalActivityDate.textContent = formattedDate;
      
      if (this.elements.modalActivityLocationSeparator) {
        this.elements.modalActivityLocationSeparator.style.display = activityLocation ? 'inline' : 'none';
      }
      
      this.elements.modal.hidden = false;
    }

    async generateCanvas() {
      if (typeof window.html2canvas !== 'function') {
        throw new Error('html2canvas library is not loaded');
      }

      const wrapper = this.querySelector('#yc-wish-modal-card-wrapper');
      if (!wrapper) {
        throw new Error('Modal card wrapper not found');
      }

      // Fix object-fit: cover issue in html2canvas by replacing img with a canvas temporarily
      const img = wrapper.querySelector('img[data-modal-image]');
      let canvasImg = null;
      let originalDisplay = '';

      if (img && img.naturalWidth) {
        originalDisplay = img.style.display;
        canvasImg = document.createElement('canvas');
        const scale = 2; // Match html2canvas scale
        canvasImg.width = img.clientWidth * scale;
        canvasImg.height = img.clientHeight * scale;
        canvasImg.style.width = img.clientWidth + 'px';
        canvasImg.style.height = img.clientHeight + 'px';
        canvasImg.style.display = 'block';
        const ctx = canvasImg.getContext('2d');
        
        const imgRatio = img.naturalWidth / img.naturalHeight;
        const canvasRatio = canvasImg.width / canvasImg.height;
        let drawWidth = canvasImg.width;
        let drawHeight = canvasImg.height;
        let offsetX = 0;
        let offsetY = 0;

        if (imgRatio > canvasRatio) {
          drawWidth = canvasImg.height * imgRatio;
          offsetX = (canvasImg.width - drawWidth) / 2;
        } else {
          drawHeight = canvasImg.width / imgRatio;
          offsetY = (canvasImg.height - drawHeight) / 2;
        }

        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        
        img.style.display = 'none';
        img.parentNode.insertBefore(canvasImg, img);
      }

      // Hide close button and action buttons temporarily for capture if needed,
      // though they are outside the wrapper.
      const canvas = await window.html2canvas(wrapper, {
        backgroundColor: null,
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        logging: false,
      });

      if (img && canvasImg) {
        canvasImg.parentNode.removeChild(canvasImg);
        img.style.display = originalDisplay;
      }

      return canvas;
    }

    async downloadImageNativeCanvas() {
      try {
        const canvas = await this.generateCanvas();
        const dataUrl = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = 'wish-card.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (e) {
        console.error('Failed to generate image', e);
        alert('Unable to save image. Please try again.');
      }
    }

    async shareImageNativeCanvas() {
      try {
        const canvas = await this.generateCanvas();
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          const file = new File([blob], 'wish-card.png', { type: 'image/png' });
          if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: 'My Wish',
              text: 'A wish sent via Kailash Energy.'
            });
          } else {
            alert('Sharing is not supported on this browser. You can save the image instead.');
          }
        }, 'image/png');
      } catch (e) {
        console.error('Failed to share image', e);
      }
    }

    startDragging(event) {
      if (!this.elements.activityListWrapper) {
        return;
      }

      this.state.isDragging = true;
      this.state.dragged = false;
      this.state.startX = event.pageX - this.elements.activityListWrapper.offsetLeft;
      this.state.scrollLeft = this.elements.activityListWrapper.scrollLeft;
    }

    stopDragging() {
      this.state.isDragging = false;
    }

    drag(event) {
      if (!this.state.isDragging || !this.elements.activityListWrapper) {
        return;
      }

      event.preventDefault();
      const x = event.pageX - this.elements.activityListWrapper.offsetLeft;
      const walk = (x - this.state.startX) * 2;

      if (Math.abs(walk) > 5) {
        this.state.dragged = true;
      }

      this.elements.activityListWrapper.scrollLeft = this.state.scrollLeft - walk;
    }
  }

  customElements.define('yc-wish', YcWish);
}
